import React from 'react';
import { ChevronLeft, ChevronRight, Search, SlidersHorizontal } from 'lucide-react';
import { Button } from '@daater/ui';
import { useDatasetsQuery, useHistoricQuery, useSwitchAccountMutation } from '../api/queries';
import { useAccountStore } from '../lib/account-store';
import { useAuthStore } from '../lib/auth-store';
import type { Dataset } from '../api/types';

type HistoricFieldValue = {
  label?: string;
  value?: string;
};

type HistoricFieldInfo = {
  _id?: string;
  fieldName?: string;
  values?: string | HistoricFieldValue[];
};

type HistoricValue = {
  dataset?: string;
  table?: string;
  fields?: Record<string, unknown>;
  fieldInfo?: HistoricFieldInfo[];
  [key: string]: unknown;
};

type HistoricRecord = {
  _id: string;
  createdAt?: string;
  value?: HistoricValue;
  [key: string]: unknown;
};

type HistoricCard = HistoricValue & {
  _id: string;
  createdAt?: string;
  section?: string;
  datasetName?: string;
  tableName?: string;
};

type SearchCardsProps = {
  currentTableId?: string;
};

const formatDateLabel = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('es-CO', {
    dateStyle: 'long',
    timeStyle: 'medium',
  }).format(date);
};

const formatDateRange = (rawValue: string) => {
  const [start, end] = rawValue.split('|');
  return `${start ?? ''} Hasta ${end ?? ''}`.trim();
};

const getFieldLabel = (field: HistoricFieldInfo) => field.fieldName ?? 'Filtro';

const getValueLabels = (values: HistoricFieldValue[]) =>
  values.map(value => value.label ?? value.value ?? '').filter(Boolean);

const getFirstDateRange = (fieldInfo?: HistoricFieldInfo[]) => {
  const dateField = fieldInfo?.find(
    field => typeof field.values === 'string' && field.values.includes('|')
  );
  if (!dateField || typeof dateField.values !== 'string') return null;
  const [start, end] = dateField.values.split('|').map(value => value.trim());
  return {
    start,
    end,
    label: getFieldLabel(dateField),
  };
};

const parseDateInput = (value: string) => {
  if (!value) return null;
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const parseDateValue = (value?: string) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const buildHistoricCards = (data: HistoricRecord[], datasets: Dataset[]) => {
  return data
    .map(report => {
      const value = report.value ?? {};
      const datasetId = value.dataset;
      const tableId = value.table;
      const dataset = datasets.find(item => item._id === datasetId);
      if (!dataset) return null;
      const table = dataset.tables?.find(item => item._id === tableId);
      if (!table) return null;
      return {
        ...value,
        _id: report._id,
        createdAt: report.createdAt,
        datasetName: dataset.name,
        tableName: table.name,
        section: dataset.section as string | undefined,
      } as HistoricCard;
    })
    .filter(Boolean) as HistoricCard[];
};

const HISTORIC_STORAGE_KEY = 'historicSearch';

export default function SearchCards({ currentTableId }: SearchCardsProps) {
  const account = useAccountStore(state => state.account);
  const authUser = useAuthStore(state => state.user);
  const accountUserId = typeof account?.user === 'string' ? account.user : account?.user?._id;
  const userId = accountUserId ?? authUser?._id;
  const switchAccountMutation = useSwitchAccountMutation();
  const { data: datasets = [] } = useDatasetsQuery(Boolean(userId));
  const { data: historics = [], isLoading } = useHistoricQuery(userId, Boolean(userId));
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const [expanded, setExpanded] = React.useState<Record<string, boolean>>({});
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);
  const [scopeFilter, setScopeFilter] = React.useState<'current' | 'other' | 'all'>(
    currentTableId ? 'current' : 'all'
  );
  const [propertyFilter, setPropertyFilter] = React.useState<
    'all' | 'country' | 'dataset' | 'table' | 'dateRange' | 'createdAt'
  >('all');
  const [textFilter, setTextFilter] = React.useState('');
  const [dateStart, setDateStart] = React.useState('');
  const [dateEnd, setDateEnd] = React.useState('');

  React.useEffect(() => {
    if (!currentTableId) {
      setScopeFilter('all');
      return;
    }
    setScopeFilter(prev => (prev === 'all' ? 'current' : prev));
  }, [currentTableId]);

  const cards = React.useMemo(() => {
    if (!datasets.length || !historics.length) return [];
    const mapped = buildHistoricCards(historics as HistoricRecord[], datasets);
    return mapped.sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bTime - aTime;
    });
  }, [datasets, historics]);

  const filteredCards = React.useMemo(() => {
    return cards.filter(card => {
      if (scopeFilter === 'current' && currentTableId && card.table !== currentTableId) {
        return false;
      }
      if (scopeFilter === 'other' && currentTableId && card.table === currentTableId) {
        return false;
      }

      if (propertyFilter === 'all') return true;

      const normalizedText = textFilter.trim().toLowerCase();
      if (propertyFilter === 'country') {
        return normalizedText ? (card.section ?? '').toLowerCase().includes(normalizedText) : true;
      }
      if (propertyFilter === 'dataset') {
        return normalizedText
          ? (card.datasetName ?? '').toLowerCase().includes(normalizedText)
          : true;
      }
      if (propertyFilter === 'table') {
        return normalizedText
          ? (card.tableName ?? '').toLowerCase().includes(normalizedText)
          : true;
      }

      const rangeStart = parseDateInput(dateStart);
      const rangeEnd = parseDateInput(dateEnd);
      if (!rangeStart && !rangeEnd) return true;

      if (propertyFilter === 'createdAt') {
        const createdAt = parseDateValue(card.createdAt);
        if (!createdAt) return false;
        if (rangeStart && createdAt < rangeStart) return false;
        if (rangeEnd && createdAt > rangeEnd) return false;
        return true;
      }

      if (propertyFilter === 'dateRange') {
        const range = getFirstDateRange(card.fieldInfo);
        const cardStart = parseDateValue(range?.start);
        const cardEnd = parseDateValue(range?.end ?? range?.start);
        if (!cardStart || !cardEnd) return false;
        if (rangeStart && cardEnd < rangeStart) return false;
        if (rangeEnd && cardStart > rangeEnd) return false;
        return true;
      }

      return true;
    });
  }, [cards, scopeFilter, currentTableId, propertyFilter, textFilter, dateStart, dateEnd]);

  React.useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    const updateScrollState = () => {
      setCanScrollLeft(node.scrollLeft > 0);
      setCanScrollRight(node.scrollLeft + node.clientWidth < node.scrollWidth - 2);
    };
    updateScrollState();
    node.addEventListener('scroll', updateScrollState, { passive: true });
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(node);
    return () => {
      node.removeEventListener('scroll', updateScrollState);
      resizeObserver.disconnect();
    };
  }, [filteredCards.length]);

  const handleScroll = (direction: 'left' | 'right') => {
    const node = scrollRef.current;
    if (!node) return;
    const delta = node.clientWidth * 0.85;
    node.scrollBy({
      left: direction === 'left' ? -delta : delta,
      behavior: 'smooth',
    });
  };

  const handleDuplicateSearch = async (search: HistoricCard) => {
    if (!account?._id || !search.dataset || !search.table) return;
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(HISTORIC_STORAGE_KEY, JSON.stringify(search));
    }
    await switchAccountMutation.mutateAsync({
      account: account._id,
      dataset: search.dataset,
      table: search.table,
    });
    if (typeof window !== 'undefined') {
      window.location.assign(`/data/table/${search.table}`);
    }
  };

  return (
    <section className="p-6 bg-white border shadow-lg rounded-xl border-pale-grey-three">
      <div className="flex flex-col gap-3 mb-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-charcoal-grey">Búsquedas recientes</h2>
          <p className="text-sm text-slate">
            {filteredCards.length
              ? `${filteredCards.length} búsquedas guardadas${
                  filteredCards.length !== cards.length ? ` de ${cards.length}` : ''
                }`
              : 'Sin búsquedas recientes.'}
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="flex flex-wrap items-center gap-2 px-3 py-2 text-xs font-semibold bg-white border rounded-full border-pale-grey-three text-slate">
            {currentTableId ? (
              <button
                type="button"
                onClick={() => setScopeFilter('current')}
                className={`rounded-full px-3 py-1 transition ${
                  scopeFilter === 'current'
                    ? 'bg-primary text-white'
                    : 'text-slate hover:text-primary'
                }`}
              >
                Tabla actual
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setScopeFilter('all')}
              className={`rounded-full px-3 py-1 transition ${
                scopeFilter === 'all' ? 'bg-primary text-white' : 'text-slate hover:text-primary'
              }`}
            >
              Todas
            </button>
            {currentTableId ? (
              <button
                type="button"
                onClick={() => setScopeFilter('other')}
                className={`rounded-full px-3 py-1 transition ${
                  scopeFilter === 'other'
                    ? 'bg-primary text-white'
                    : 'text-slate hover:text-primary'
                }`}
              >
                Otras tablas
              </button>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <select
              className="py-3 pl-3 text-xs font-semibold bg-white rounded-full border-pale-grey-three text-charcoal-grey"
              value={propertyFilter}
              onChange={event => {
                const value = event.target.value as typeof propertyFilter;
                setPropertyFilter(value);
                setTextFilter('');
                setDateStart('');
                setDateEnd('');
              }}
            >
              <option value="all">Todos los filtros</option>
              <option value="country">País</option>
              <option value="dataset">Base</option>
              <option value="table">Tabla</option>
              <option value="dateRange">Rango de fechas</option>
              <option value="createdAt">Fecha búsqueda</option>
            </select>
            {propertyFilter === 'country' ||
            propertyFilter === 'dataset' ||
            propertyFilter === 'table' ? (
              <input
                className="px-3 py-2 text-xs bg-white border rounded-full w-44 border-pale-grey-three text-charcoal-grey"
                placeholder="Buscar..."
                value={textFilter}
                onChange={event => setTextFilter(event.target.value)}
              />
            ) : null}
            {propertyFilter === 'dateRange' || propertyFilter === 'createdAt' ? (
              <div className="flex items-center gap-2 text-xs text-slate">
                <input
                  type="date"
                  className="px-3 py-2 text-xs bg-white border rounded-full border-pale-grey-three text-charcoal-grey"
                  value={dateStart}
                  onChange={event => setDateStart(event.target.value)}
                />
                <span className="text-slate">a</span>
                <input
                  type="date"
                  className="px-3 py-2 text-xs bg-white border rounded-full border-pale-grey-three text-charcoal-grey"
                  value={dateEnd}
                  onChange={event => setDateEnd(event.target.value)}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="px-4 py-8 text-sm text-center border border-dashed rounded-lg border-pale-grey-three bg-pale-grey/40 text-slate">
          Cargando búsquedas recientes...
        </div>
      ) : filteredCards.length ? (
        <div className="relative">
          {canScrollLeft && (
            <button
              type="button"
              aria-label="Desplazar a la izquierda"
              onClick={() => handleScroll('left')}
              className="absolute left-0 top-1/2 z-10 -translate-y-1/2 inline-flex h-10 w-10 items-center justify-center rounded-full border border-pale-grey-three bg-white/90 text-charcoal-grey shadow-lg backdrop-blur-sm transition hover:text-primary"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}
          {canScrollRight && (
            <button
              type="button"
              aria-label="Desplazar a la derecha"
              onClick={() => handleScroll('right')}
              className="absolute right-0 top-1/2 z-10 -translate-y-1/2 inline-flex h-10 w-10 items-center justify-center rounded-full border border-pale-grey-three bg-white/90 text-charcoal-grey shadow-lg backdrop-blur-sm transition hover:text-primary"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          )}
          <div
            ref={scrollRef}
            className="flex gap-4 pb-2 pr-1 overflow-x-auto scroll-smooth snap-x snap-mandatory"
          >
            {filteredCards.map(historic => {
              const fieldInfo = historic.fieldInfo ?? [];
              const dateRange = getFirstDateRange(fieldInfo);
              const valueFields = fieldInfo.filter(field => Array.isArray(field.values));
              const compactValueFields = valueFields.slice(0, 2);
              const isExpanded = Boolean(expanded[historic._id]);
              const compactItems = [
                dateRange
                  ? {
                      label: dateRange.label,
                      value: formatDateRange(`${dateRange.start}|${dateRange.end}`),
                    }
                  : null,
                ...compactValueFields.map(field => {
                  const values = Array.isArray(field.values) ? getValueLabels(field.values) : [];
                  return {
                    label: getFieldLabel(field),
                    value: values[0] ?? '-',
                  };
                }),
              ].filter(Boolean) as Array<{ label: string; value: string }>;

              return (
                <article
                  key={historic._id}
                  className="flex w-[360px] shrink-0 snap-start flex-col rounded-xl border border-pale-grey-three bg-white p-3 shadow-sm sm:w-[420px] lg:w-[520px]"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                    <div className="text-xs font-semibold text-slate">
                      {formatDateLabel(historic.createdAt)}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        className="p-0 bg-transparent rounded-full shadow-none h-9 w-9 text-primary hover:bg-primary/10"
                        aria-label="Realizar búsqueda"
                        title="Realizar búsqueda"
                        onClick={() => void handleDuplicateSearch(historic)}
                      >
                        <Search className="w-5 h-5" />
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="p-0 bg-transparent rounded-full shadow-none h-9 w-9 text-slate hover:bg-slate/10"
                        aria-label={isExpanded ? 'Ocultar filtros' : 'Mostrar filtros'}
                        title={isExpanded ? 'Ocultar filtros' : 'Mostrar filtros'}
                        onClick={() =>
                          setExpanded(prev => ({
                            ...prev,
                            [historic._id]: !prev[historic._id],
                          }))
                        }
                      >
                        <SlidersHorizontal className="w-5 h-5" />
                        {/* <ChevronDown
                        className={`h-5 w-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      /> */}
                      </Button>
                    </div>
                  </div>

                  <div className="grid gap-3 text-xs text-slate sm:grid-cols-[1.2fr_0.8fr]">
                    <div className="space-y-2">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate/70">
                        Contexto
                      </div>
                      <div className="text-xs text-slate">
                        <span className="font-semibold text-charcoal-grey">País:</span>{' '}
                        {historic.section ?? '-'}
                        <span className="px-1 text-slate/50">•</span>
                        <span className="font-semibold text-charcoal-grey">Base:</span>{' '}
                        {historic.datasetName ?? '-'}
                      </div>
                      <div className="text-xs text-slate">
                        <span className="font-semibold text-charcoal-grey">Tabla:</span>{' '}
                        {historic.tableName ?? '-'}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate/70">
                        Filtros clave
                      </div>
                      {compactItems.length ? (
                        <div className="grid gap-2">
                          {compactItems.map(item => (
                            <div key={`${historic._id}-${item.label}`} className="text-xs">
                              <span className="font-semibold text-charcoal-grey">
                                {item.label}:
                              </span>{' '}
                              <span className="text-slate">{item.value}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-xs text-slate">Sin filtros clave.</div>
                      )}
                    </div>
                  </div>

                  {isExpanded && fieldInfo.length ? (
                    <div className="p-3 mt-4 space-y-3 text-xs border rounded-lg border-pale-grey-three bg-pale-grey/40 text-slate">
                      {fieldInfo.map(field => {
                        const label = getFieldLabel(field);
                        if (Array.isArray(field.values)) {
                          const values = getValueLabels(field.values);
                          return (
                            <div key={field._id ?? field.fieldName}>
                              <div className="font-semibold text-charcoal-grey">{label}</div>
                              <ul className="mt-2 space-y-1">
                                {values.map(value => (
                                  <li key={value} className="text-slate">
                                    {value}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          );
                        }
                        if (typeof field.values === 'string') {
                          return (
                            <div key={field._id ?? field.fieldName}>
                              <div className="font-semibold text-charcoal-grey">{label}</div>
                              <div className="mt-1 text-slate">{formatDateRange(field.values)}</div>
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="px-4 py-8 text-sm text-center border border-dashed rounded-lg border-pale-grey-three bg-pale-grey/40 text-slate">
          No hay búsquedas recientes para mostrar.
        </div>
      )}
    </section>
  );
}
